import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  IconButton,
  VStack,
  Button,
  Heading,
  useToast,
} from "@chakra-ui/react";
import * as Sentry from "@sentry/browser";
import { SylaApiError } from "@syla/shared/errors/SylaApiError";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import {
  PopulatedWallet,
  WalletImportMethod,
  SyncState,
  getWalletName,
} from "@syla/shared/types/models/WalletBase";
import axios from "axios";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { getDirtyValues } from "../../../../helper/forms/getDirtyValues";
import { updateWallet, refreshWallets } from "../../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { AuthoriseConnectionButton } from "../components/AuthoriseConnectionButton";
import { DataSourceDrawerFooter } from "../components/DataSourceDrawerFooter";
import { DataSourceDrawHeader } from "../components/DataSourceDrawerHeader";
import { DataSourceNameField } from "../components/DataSourceNameField";
import { DataSourceTitle } from "../components/DataSourceTitle";
import { ImportErrorAlert } from "../components/ImportErrorAlert";
import { InstructionsSection } from "../components/InstructionsSection";
import { SyncApiFields } from "../components/SyncApiFields";
import { WalletSyncErrorBox } from "../components/WalletSyncErrorBox";

/**
 * Editing allows changing the name of the data source.
 * For data sources with API keys, you can edit the api keys.
 * For data sources with OAuth, you can re-auth.
 *
 * For data sources without a sync connection, there is nothing additional to edit.
 */
export function EditDataSourceWorkflow({
  wallet,
  onBack,
}: {
  wallet: PopulatedWallet;
  onBack: () => void;
}): React.ReactElement {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const defaultValues = useMemo(
    () => ({
      apiKey: wallet.apiKey || "",
      secretKey: wallet.secretKey || "",
      passphrase: wallet.passphrase || "",
      customName: getWalletName({ wallet, dataSource: wallet.dataSource }),
    }),
    [wallet],
  );

  const form = useForm<EditDataSourceForm>({ defaultValues });

  const toast = useToast();

  // The user needs to have instructions available if creating a new API key.
  const enableInstructions = wallet.importMethod === WalletImportMethod.API;

  const [submitError, setSubmitError] = useState<
    Error | SylaApiError | unknown | undefined
  >();

  // after the OAuth is successful, we need to refresh the wallet state.
  const [isPendingRefresh, setIsPendingRefresh] = useState(false);

  const isOauthError = !(wallet.authorised || isPendingRefresh);

  const handleAuthorised = useCallback(async () => {
    setIsPendingRefresh(true);

    await refreshWallets(accountId);

    setIsPendingRefresh(false);
  }, [accountId]);

  // reset defaulValues if they change (due to wallet prop changing)
  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  // on submit handles custom name update, and api key updates, it does not
  // handle the oauth flow.
  const onSubmit = async (data: EditDataSourceForm) => {
    setSubmitError(undefined);

    const updatedInputs = getDirtyValues(form.formState.dirtyFields, data);

    if (!Object.keys(updatedInputs).length) {
      onBack();
      return;
    }

    // all import types work the same way when editing/ updating details.
    try {
      await updateWallet(accountId, wallet._id, {
        apiKey: updatedInputs.apiKey,
        secretKey: updatedInputs.secretKey,
        passphrase: updatedInputs.passphrase,
        customName:
          form.formState.dirtyFields.customName ?
            updatedInputs.customName
          : undefined,
      });

      toast({
        title: "Changes saved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // close the drawer.
      // close in here and not at the end of the function so that it doesn't close on an error.
      onBack();
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        Sentry.captureException(error);
      }

      setSubmitError(error);
    }
  };

  return (
    <FormProvider {...form}>
      <DataSourceDrawHeader>
        <IconButton
          variant="ghost"
          aria-label="Back"
          icon={<ArrowBackIcon />}
          boxSize="2.5rem"
          fontSize="2xl"
          onClick={onBack}
        />
        <DataSourceTitle
          name={form.watch("customName")}
          image={wallet.dataSource.image}
        />
      </DataSourceDrawHeader>

      <VStack
        w="full"
        px={4}
        py={4}
        overflowY="auto"
        flex={1}
        align="flex-start"
        spacing={0}
        gap={8}
      >
        <ImportErrorAlert
          error={submitError}
          isCustomDataSource={wallet.dataSource.type === DataSourceType.Custom}
          onClose={() => setSubmitError(undefined)}
        />

        {enableInstructions && (
          <InstructionsSection
            w="full"
            dataSource={wallet.dataSource}
            importMethod={DataSourceInputType.Api}
            isNewDataSource={false}
          />
        )}

        <VStack
          w="full"
          align="flex-start"
          id="edit-data-source-form"
          as="form"
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          spacing={4}
        >
          <Heading as="h2" size="md">
            Edit data source
          </Heading>

          <DataSourceNameField name="customName" form={form} />

          {wallet.importMethod === WalletImportMethod.API &&
            wallet.dataSource.syncAuthType !== "oauth" && (
              <VStack w="full" align="flex-start" spacing={4}>
                <SyncApiFields dataSource={wallet.dataSource} />
              </VStack>
            )}

          {wallet.importMethod === WalletImportMethod.API &&
            wallet.dataSource.syncAuthType === "oauth" &&
            isOauthError && (
              <AuthoriseConnectionButton
                w="full"
                walletId={wallet._id}
                dataSource={wallet.dataSource}
                customName={form.watch("customName")}
                onAuthorised={handleAuthorised}
                onError={setSubmitError}
              />
            )}

          {isOauthError &&
            wallet.syncState !== SyncState.InProgress &&
            wallet.importMethod === WalletImportMethod.API && (
              <WalletSyncErrorBox wallet={wallet} />
            )}
        </VStack>
      </VStack>

      <DataSourceDrawerFooter>
        <Button variant="outline" colorScheme="gray" onClick={onBack}>
          Back
        </Button>
        <Button
          form="edit-data-source-form"
          variant="solid"
          colorScheme="primary"
          isDisabled={!form.formState.isDirty}
          isLoading={form.formState.isSubmitting}
          type="submit"
        >
          Save
        </Button>
      </DataSourceDrawerFooter>
    </FormProvider>
  );
}

type EditDataSourceForm = {
  apiKey: string;
  secretKey: string;
  passphrase: string;
  customName: string;
};
